import { AnimatePresence } from 'framer-motion';
import { FC, memo, useEffect, useState } from 'react';

import DemilkedIcon from '@/assets/icons/cover-images/demilked-icon-red.png';
import DemilkedWatermark from '@/assets/icons/cover-images/demilked-watermark.png';
import WhiteTriangle from '@/assets/icons/white-triangle.png';
import useAlerts from '@/hooks/useAlerts';
import {
  downloadImagesAsZip,
  downloadSelectedImagesAsZip,
  generateImagesInParallel,
  generateSingleImage,
} from '@/utils/coverImages/imageGeneration';
import { distributeTextCanvas } from '@/utils/coverImages/textDistribution';
import { replaceQuotes } from '@/utils/helpers';

import { ImageVariation } from '.';
import FloatingBox from '../FloatingBox';
import ImageResults from '../ImageResults';
import PostToArticleButton from '../PostToArticleButton';
import Progress from '../Progress';

interface Props {
  variations?: ImageVariation[];
  images?: string[];
  setImages: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  inArticle?: boolean;
  onReset: () => void;
  isAiGenerated?: boolean;
}

const GeneratedImages: FC<Props> = memo(
  ({ variations, images, setImages, inArticle, onReset, isAiGenerated }) => {
    const alerts = useAlerts();

    const [selectedImages, setSelectedImages] = useState<number[]>([]);
    const [counter, setCounter] = useState(0);
    const [showProgress, setShowProgress] = useState(false);

    const generateImageVariations = async (
      variation: ImageVariation,
    ): Promise<string | undefined> => {
      try {
        return await generateSingleImage(
          async () => {
            const imageHolder = document.createElement('div');
            imageHolder.classList.add('image-holder-var14');

            // Wait for font to load before measuring text
            await document.fonts.ready;

            if (variation.topText) {
              const topText = document.createElement('div');

              topText.classList.add('top-text-var14');
              const logo = new Image();
              logo.classList.add('logo-icon-var14');
              logo.src = DemilkedIcon;
              topText.appendChild(logo);

              const topTextP = document.createElement('p');
              topText.appendChild(topTextP);

              let text = variation.topText;
              if (variation.topTextBrakes) {
                topTextP.innerText = text;
              } else {
                topTextP.innerHTML = text;
              }

              imageHolder.appendChild(topText);
            }

            if (variation.bottomText) {
              const bottomText = document.createElement('div');
              const bottomTextP = document.createElement('p');
              bottomText.classList.add('bottom-text-var14');
              let text = `“${replaceQuotes(variation.bottomText)}”`;
              if (variation.bottomTextBrakes) {
                bottomTextP.innerText = text;
              } else {
                text = distributeTextCanvas(text, {
                  maxWidth: 600,
                  fontFamily: 'CustomArial',
                  minFontSize:
                    variation.fontSize !== 0 ? variation.fontSize! : 38,
                });
                bottomTextP.innerHTML = text;
              }
              bottomText.appendChild(bottomTextP);

              const triangle = new Image();
              triangle.classList.add('triangle-icon-var14');
              triangle.src = WhiteTriangle;
              bottomTextP.appendChild(triangle);

              imageHolder.appendChild(bottomText);

              if (variation.fontSize) {
                bottomText.style.fontSize = `${variation.fontSize}px`;
              }
            }
            if (variation.watermark) {
              const watermark = new Image();
              watermark.classList.add('watermark-var14');
              watermark.src = DemilkedWatermark;
              imageHolder.appendChild(watermark);
            }

            const fullImage = new Image();
            fullImage.src = variation.imageSrc;
            fullImage.classList.add('full-image-var14');
            imageHolder.appendChild(fullImage);

            return imageHolder;
          },
          {
            holderClassName: 'generated-images-holder-var14',
            pixelRatio: 1,
          },
        );
      } catch (e) {
        alerts.error();
        return undefined;
      }
    };

    const generateAllVariations = async () => {
      if (!variations?.length) return;

      setShowProgress(true);
      setCounter(0);

      const validImages = await generateImagesInParallel(
        variations,
        generateImageVariations,
        (current) => setCounter(current),
      );

      setImages(validImages);
      setShowProgress(false);
    };

    const handleDownloadZip = async (selected: boolean) => {
      if (!images) return;

      if (selected) {
        await downloadSelectedImagesAsZip(images, selectedImages);
      } else {
        await downloadImagesAsZip(images);
      }
    };

    useEffect(() => {
      generateAllVariations();
      // eslint-disable-next-line
    }, [variations]);

    useEffect(() => {
      if (!images?.length) return;
      setSelectedImages(images?.map((_, index) => index));
    }, [images]);

    return (
      <>
        <ImageResults
          images={images}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          setImages={setImages}
        />
        <AnimatePresence>
          {!!showProgress && !!variations?.length && (
            <Progress
              key={1}
              progressPercent={(counter / variations.length) * 100}
            />
          )}
          {!inArticle ? (
            <>
              {!!images?.length && (
                <FloatingBox
                  key={2}
                  selectedImages={selectedImages}
                  unselectAll={() => setSelectedImages([])}
                  selectAll={() =>
                    setSelectedImages(images?.map((_, index) => index))
                  }
                  onDownload={handleDownloadZip}
                  images={images}
                />
              )}
            </>
          ) : (
            <>
              {!!images?.length && (
                <PostToArticleButton
                  images={images}
                  selectedImages={selectedImages}
                  onReset={onReset}
                  isAiGenerated={isAiGenerated}
                />
              )}
            </>
          )}
        </AnimatePresence>
      </>
    );
  },
);

export default GeneratedImages;
